import React, { ReactElement, useEffect, useState } from 'react';
import {
  Pressable, StyleSheet, Text, TouchableOpacity, View, Animated as RNAnimated,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS, useAnimatedReaction, useAnimatedStyle, useSharedValue, withTiming,
} from 'react-native-reanimated';
import { useDispatch, useSelector } from 'react-redux';
import { Polygon, Svg } from 'react-native-svg';
import { useNavigation } from '@react-navigation/native';
import EllipsisIconVertical from '../EllipsisIconVertical';
import Colors from '../../constants/Colors';
import MoveIcon from '../16pxIcons/MoveIcon';
// import PriorityFlag from '../16pxIcons/PriorityFlag';
import RightArrow24 from '../24pxIcons/RightArrow24';
import editArea from '../../requests/Locations/Areas/EditArea';
import editSpace from '../../requests/Locations/Spaces/EditSpace';
import deleteArea from '../../requests/Locations/Areas/DeleteArea';
import deleteSpace from '../../requests/Locations/Spaces/DeleteSpace';
import LocationIcon from './LocationIcon';
import LocationView from './LocationView';
import isMobile from '../IsMobile';
import { LocationSubtypeTitles } from './SetFloorsModal/FloorItem';
import { DateRange, isLiveMode } from '../../redux/dateRangeSlice';
import {
  DELETE_AREA_SUCCESS, DELETE_SPACE_SUCCESS, REMOVE_AREA_SUCCESS, REMOVE_SPACE_SUCCESS,
  SAVE_POSITION_SUCCESS, SOMETHING_WENT_WRONG,
} from '../../constants/AlertMessages';
import { updateLocations } from '../../redux/locationStructureSlice';
import LocationIconV2 from './LocationIconV2';
import Style from '../../constants/Style';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import BouncingDots from '../BouncingDots';

export default function PinIcon(props) {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const buildings: object | null | undefined = useSelector((state:
    { locations }) => state.locations.buildings);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const [isFocused, setIsFocused] = useState<boolean>(false);

  const translateXarea = useSharedValue<number>(0);
  const translateYarea = useSharedValue<number>(0);

  const negativeScale = useSharedValue<number>(props.negativeScale ?? 1);

  const occupancyPercentage: number | null = (typeof props.area.occupancy === 'number'
    && typeof props.capacity === 'number' && props.capacity > 0) ? Math.round(100 * (props.area.occupancy / props.capacity))
    : null;
  const isMovingThisLocation: boolean = props.movingLocationId === props.area.LocationId;
  const isAnyLocationMoving: boolean = !!props.movingLocationId;
  const isHoverFocused: boolean = isFocused && !isAnyLocationMoving;
  const showLocationInfo: boolean = isHoverFocused || menuOpen || isMovingThisLocation;
  const dialogZIndex: number = isMovingThisLocation ? 6 : 1;
  let infoZIndex = -1;
  if (isHoverFocused || isMovingThisLocation) infoZIndex = 10;
  if (isMobile) infoZIndex = 3;
  const dialogOpacity: number = (isHoverFocused || !props.ticketsSelected || isMovingThisLocation)
    ? 1 : 0.5;

  useAnimatedReaction(
    () => props.focusedLocationId?.value === props.area.LocationId,
    (focusedValue, previousFocusedValue) => {
      if (focusedValue !== previousFocusedValue) {
        runOnJS(setIsFocused)(focusedValue);
      }
    },
  );

  useEffect(() => {
    if (!props.editMode) {
      // props.setPinMenuOpen('');
      setMenuOpen(false);
    }
  }, [props.editMode]);

  useEffect(() => {
    negativeScale.value = withTiming(props.negativeScale);
  }, [props]);

  const panGestureArea = Gesture.Pan()
    .onChange((event) => {
      translateXarea.value += event.changeX;
      translateYarea.value += event.changeY;
    })
    .onEnd(() => {
      if (!Array.isArray(props.area.points) || props.area.points.length === 0) {
        props.showAlert('Location has no map points.', true);
        return;
      }

      const areaToEdit = {
        ...props.area,
        points: props.area.points.map((point) => ({ ...point })),
      };
      let error = false;
      areaToEdit.points.forEach((point, i) => {
        if (point.number === i) {
          areaToEdit.points[i].x = point.x + translateXarea.value;
          areaToEdit.points[i].y = point.y + translateYarea.value;

          if (
            areaToEdit.points[i].x < 0
            || areaToEdit.points[i].y < 0
            || areaToEdit.points[i].x > props.imageWidth
            || areaToEdit.points[i].y > props.imageHeight
          ) {
            error = true;
            areaToEdit.points[i].x = point.x - translateXarea.value;
            areaToEdit.points[i].y = point.y - translateYarea.value;
          }
        }

        if (props.areaSelected) {
          let inside = false;
          const x = point.x + translateXarea.value;
          const y = point.y + translateYarea.value;
          const points = props.parentArea?.points ?? [];
          for (
            let ii = 0, j = points.length - 1;
            ii < points.length;
            // eslint-disable-next-line no-plusplus
            j = ii++
          ) {
            const xi = props.parentArea.points[ii].x;
            const yi = props.parentArea.points[ii].y;
            const xj = props.parentArea.points[j].x;
            const yj = props.parentArea.points[j].y;

            const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
            if (intersect) inside = !inside;
          }

          if (!inside) {
            error = true;
            areaToEdit.points[i].x = point.x - translateXarea.value;
            areaToEdit.points[i].y = point.y - translateYarea.value;
          }
        }
      });
      if (error) {
        props.showAlert('Out of bounds!', true);
      } else {
        setLoading(true);
        const deltaX = translateXarea.value;
        const deltaY = translateYarea.value;
        areaToEdit.top = props.getMinY(areaToEdit.points);
        areaToEdit.left = props.getMinX(areaToEdit.points);
        areaToEdit.right = props.getMaxX(areaToEdit.points);
        areaToEdit.bottom = props.getMaxY(areaToEdit.points);
        areaToEdit.polygon = props.getPointsString(areaToEdit.points);
        if (!props.isSpace) {
          areaToEdit.pinX = (areaToEdit.pinX ?? props.area.pinX ?? 0) + deltaX;
          areaToEdit.pinY = (areaToEdit.pinY ?? props.area.pinY ?? 0) + deltaY;
        }
        editArea({
          LocationId: props.area.LocationId,
          Area: {
            Name: props.area.name,
            Top: areaToEdit.top,
            Left: areaToEdit.left,
            Right: areaToEdit.right,
            Bottom: areaToEdit.bottom,
            Points: areaToEdit.points,
            Polygon: areaToEdit.polygon,
            Priority: props.area.highPriority,
            ParentId: props.area.ParentId,
            LocationSubtype: props.area.LocationSubtype,
            LocationCapacity: props.area.LocationCapacity,
          },
        }).then((response) => {
          if (response.isError) {
            props.showAlert(SOMETHING_WENT_WRONG, true);
            setLoading(false);
          } else {
            const updatedAreas = props.areas.map((area) => (
              area.LocationId === areaToEdit.LocationId ? areaToEdit : area
            ));
            props.setAreas(updatedAreas);
            props.showAlert(SAVE_POSITION_SUCCESS, false);
            translateXarea.value = 0;
            translateYarea.value = 0;
            props.finishedMovingArea();
            setLoading(false);
          }
        });
      }
    });

  const transformStyleArea = useAnimatedStyle(() => ({
    // @ts-ignore
    transform: [
      {
        translateX: translateXarea.value,
      },
      {
        translateY: translateYarea.value,
      },
    ],
  }));

  const negativeScaleStyle = useAnimatedStyle(() => ({
    transform: [
      {
        scale: negativeScale.value,
      },
    ],
  }));

  const removeFromMap = (area) => props.showAlert(
    `Are you sure you want to remove "${area.name}" from map?`,
    true,
    () => {
      const newArray = Object.assign([], props.areas);
      newArray.forEach((newArea) => {
        if (newArea.LocationId === props.area.LocationId) {
          newArea.polygon = '';
          newArea.points = [];
        }
      });
      setLoading(true);
      if (props.isSpace) {
        editSpace({
          LocationId: props.area.LocationId,
          Space: {
            Name: props.area.name,
            Top: 0,
            Left: 0,
            Right: 0,
            Bottom: 0,
            Priority: props.area.highPriority,
            Points: null,
            Polygon: '',
            ParentId: props.area.ParentId,
            LocationSubtype: props.area.LocationSubtype,
            LocationCapacity: props.area.LocationCapacity,
            ExternalId: props.area.ExternalId,
          },
        }).then((response) => {
          if (response?.isError) {
            props.showAlert(JSON.stringify(response.errorMessage), true);
            setMenuOpen(false);
            props.setPinMenuOpen('');
          } else {
            props.showAlert(REMOVE_SPACE_SUCCESS, false);
            props.setAreas(newArray);
            setMenuOpen(false);
            props.setPinMenuOpen('');
          }
          setLoading(false);
        });
      } else {
        editArea({
          LocationId: props.area.LocationId,
          Area: {
            Name: props.area.name,
            Top: 0,
            Left: 0,
            Right: 0,
            Bottom: 0,
            Points: null,
            Polygon: '',
            Priority: props.area.highPriority,
            ParentId: props.area.ParentId,
            LocationSubtype: props.area.LocationSubtype,
            LocationCapacity: props.area.LocationCapacity,
          },
        }).then((response) => {
          if (response.isError) {
            props.showAlert(JSON.stringify(response.errorMessage), true);
            setMenuOpen(false);
            props.setPinMenuOpen('');
          } else {
            props.showAlert(REMOVE_AREA_SUCCESS, false);
            props.setAreas(newArray);
            setMenuOpen(false);
            props.setPinMenuOpen('');
          }
          setLoading(false);
        });
      }
    },
    'Remove',
  );

  const deleteLocation = (space) => props.showAlert(
    `Delete ${props.isSpace ? 'space' : 'area'}?`,
    true,
    () => {
      const locationId: string = props.area.LocationId;
      const parentId: string = props.area.ParentId;
      const newArray = props.areas.filter((item) => item.LocationId !== locationId);
      setLoading(true);
      if (props.isSpace) {
        deleteSpace(locationId).then((response) => {
          if (response?.isError) {
            props.showAlert(response?.errorText, true);
            setMenuOpen(false);
            props.setPinMenuOpen('');
          } else {
            props.showAlert(DELETE_SPACE_SUCCESS, false);
            props.setAreas(newArray);
            setMenuOpen(false);
            props.setPinMenuOpen('');
            props.areaUpdated();
            if (props.parentFloor?.LocationId) {
              const updatedBuildings = structuredClone(buildings);
              delete updatedBuildings[selectedBuildingId].floors[props.parentFloor?.LocationId]
                .areas[parentId].spaces[locationId];
              dispatch(updateLocations(updatedBuildings));
            }
          }
          setLoading(false);
        });
      } else {
        deleteArea(locationId).then((response) => {
          if (response.isError) {
            props.showAlert(response.errorText, true);
            setMenuOpen(false);
            props.setPinMenuOpen('');
          } else {
            props.showAlert(DELETE_AREA_SUCCESS, false);
            props.setAreas(newArray);
            setMenuOpen(false);
            props.setPinMenuOpen('');
            props.areaUpdated();
            const updatedBuildings = structuredClone(buildings);
            delete updatedBuildings[selectedBuildingId].floors[parentId].areas[locationId];
            dispatch(updateLocations(updatedBuildings));
          }
          setLoading(false);
        });
      }
    },
    'Delete',
    <Text>
      {'Do you want to delete '}
      <Text style={Style.alertBoldText}>{space.name}</Text>
      {' from '}
      <Text style={Style.alertBoldText}>{props.parentArea?.name ?? props.parentFloor.Name}</Text>
      {'?'}
    </Text>,
  );

  const detailLabel = (): string => {
    if (props.occupancyShown) return isLiveMode(range) ? 'Occupancy: ' : 'Average occupancy: ';
    if (props.ticketsSelected) return 'Tickets: ';
    if (props.assetsSelected) return 'Assets: ';
    return 'Sensors & Meters: ';
  };

  const detailValue = (): string => {
    if (props.occupancyShown) {
      if (occupancyPercentage !== null && occupancyPercentage === 0) return '0%';
      if (occupancyPercentage !== null && occupancyPercentage < 1) return '<1%';
      return `${occupancyPercentage ?? '0'} %`;
    }
    if (props.ticketsSelected) return `${props.ticketCount ?? 0}`;
    if (props.assetsSelected) return `${props.area.assetCount ?? 0}`;
    return `${props.area.sensorCount ?? 0} + ${props.area.meterCount ?? 0}`;
  };

  const occupancyPinText = (): string => {
    const occupancyValue = typeof props.area.occupancy === 'number'
      ? props.area.occupancy
      : null;
    const capacityValue = typeof props.capacity === 'number' && props.capacity >= 0
      ? props.capacity
      : null;
    const roundedOccupancyValue = occupancyValue !== null ? Math.round(occupancyValue) : null;
    const roundedCapacityValue = capacityValue !== null ? Math.round(capacityValue) : null;

    if (!isLiveMode(range)) {
      if (occupancyPercentage !== null && occupancyPercentage === 0) return '0%';
      if (occupancyPercentage !== null && occupancyPercentage < 1) return '<1%';
      if (occupancyPercentage !== null) return `${occupancyPercentage}%`;
      if (occupancyValue !== null) return `${occupancyValue}`;
      return '0%';
    }

    if (roundedOccupancyValue !== null && roundedCapacityValue !== null) {
      return `${roundedOccupancyValue}/${roundedCapacityValue}`;
    }

    if (roundedOccupancyValue !== null) return `${roundedOccupancyValue}`;
    if (roundedCapacityValue !== null) return `0/${roundedCapacityValue}`;

    return '0';
  };

  const locationInfo = () => (
    <Animated.View
      testID='map-area-modal'
      style={[
        {
          marginBottom: props.area.top < 120 ? -120 : 32,
          left: -52,
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: -1,
          top: 42 * props.negativeScale,
          paddingEnd: 24,
          borderTopStartRadius: 60,
          borderTopEndRadius: 90,
          // backgroundColor: 'red', // TODO: remove
        },
        negativeScaleStyle,
      ]}
    >
      <View style={[styles.locationView, { opacity: isMovingThisLocation ? 0.9 : 1 }]}>
        <View style={{ padding: 8 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            {/* <View
              style={{
                flexDirection: 'row',
                borderWidth: 1,
                borderRadius: 4,
                borderColor: props.area.highPriority
                  ? Colors.light.hereworksMediumYellow
                  : Colors.light.hereworksCheckGreen,
                alignItems: 'center',
                padding: 3,
                backgroundColor: '#fff',
                marginEnd: 8,
                alignSelf: 'flex-start',
              }}
            >
              <PriorityFlag
                color={
                  props.area.highPriority
                    ? Colors.light.hereworksMediumYellow
                    : Colors.light.hereworksCheckGreen
                }
              />
              <Text
                style={{
                  color: props.area.highPriority
                    ? Colors.light.hereworksMediumYellow
                    : Colors.light.hereworksCheckGreen,
                  fontFamily: 'Roboto_500Medium',
                  fontSize: 12,
                  marginLeft: 2,
                }}
              >
                {props.area.highPriority ? 'H' : 'N'}
              </Text>
            </View> */}
            <View style={styles.nameContainer}>
              <Text style={styles.name}>
                {props.area.name}
              </Text>
              <Text style={styles.locationType}>
                {LocationSubtypeTitles[props.area.LocationSubtype] ?? 'Area'}
              </Text>
            </View>
            {props.editMode && (
              <TouchableOpacity
                onPress={() => {
                  if (!menuOpen) props.setPinMenuOpen(props.area.LocationId);
                  setMenuOpen(!menuOpen);
                }}
                testID='map-edit-area-menu-btn'
              >
                <EllipsisIconVertical />
              </TouchableOpacity>
            )}
            {!props.editMode && (
              <TouchableOpacity
                onPress={props.arrowPress}
                testID='map-navigate-to-asset-from-map-modal-btn'
              >
                <RightArrow24 />
              </TouchableOpacity>
            )}
          </View>
          {(props.areaSelected === false && !props.isSpace && !props.smartGuard) && (
            <View style={{ marginTop: 12, flexDirection: 'row' }}>
              <Text
                style={styles.label}
                testID={props.occupancyShown ? 'capacity-label' : 'spaces-label'}
              >
                {`${props.occupancyShown ? 'Capacity' : 'Spaces'}: `}
              </Text>
              <Text style={styles.value}>
                {`${props.occupancyShown ? props.capacity ?? '-' : props.area.spaceCount ?? 0}`}
              </Text>
            </View>
          )}
          {props.smartGuard ? null : <View style={{ marginTop: 4, flexDirection: 'row' }}>
            <Text
              style={styles.label}
              testID='avg-occupancy-label'
            >
              {detailLabel()}
            </Text>
            <Text style={styles.value}>
              {detailValue()}
            </Text>
          </View>}
        </View>
        <GestureDetector gesture={panGestureArea}>
          <Pressable
            style={(state) => [
              {
                opacity: props.movingLocationId === props.area.LocationId ? 1 : 0,
                zIndex: state.hovered ? 10 : 3,
                position: 'absolute',
                bottom: -12,
                right: -12,
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                borderColor: Colors.light.hereworksBlue100,
                borderWidth: 1,
                borderRadius: 8,
                backgroundColor: state.hovered
                  ? Colors.light.hereworksLightBlue
                  : Colors.light.hereworksWhite100,
              },
            ]}
            disabled={!isMovingThisLocation}
            onPressIn={() => {
              props.setMovingItem(true);
            }}
          >
            {({ hovered }): ReactElement => (
              <View>
                <MoveIcon color={hovered ? '#fff' : Colors.light.hereworksBlue300} />
                {loading && (
                  <View
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: '#ffffff50',
                    }}
                  >
                    <BouncingDots />
                  </View>
                )}
              </View>
            )}
          </Pressable>
        </GestureDetector>
        {(menuOpen && props.editMode) && (
          <View
            style={{
              position: 'absolute',
              top: 40,
              right: -60,
              backgroundColor: '#fff',
              borderRadius: 8,
              borderColor: Colors.light.hereworksBlue200,
              borderWidth: 1,
              zIndex: 4,
            }}
          >
            <TouchableOpacity
              onPress={() => {
                setMenuOpen(false);
                props.setPinMenuOpen('');
                props.editArea();
              }}
              testID='map-edit-area-modal-btn'
            >
              <Text style={styles.menuText}>
                {props.isSpace ? 'Edit space' : 'Edit area'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => {
                setMenuOpen(false);
                props.setPinMenuOpen('');
                props.setMovingItem(true);
              }}
            >
              <Text style={styles.menuText}>
                {props.isSpace ? 'Move space' : 'Move area'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => removeFromMap(props.area)}
              testID='map-remove-area-modal-btn'
            >
              <Text style={styles.menuText}>
                {props.isSpace ? 'Remove space from map' : 'Remove area from map'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => {
                navigation.navigate(
                  AppScreen.LOCATION_MANAGEMENT as any,
                  { locationId: props.space?.LocationId ?? props.area?.LocationId },
                );
              }}
            >
              <Text style={styles.menuText}>
                {`View ${props.isSpace ? 'space' : 'area'} in location management`}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => deleteLocation(props.area)}
              testID='map-delete-area-modal-btn'
            >
              <Text style={styles.menuText}>
                {props.isSpace ? 'Delete space' : 'Delete area'}
              </Text>
            </TouchableOpacity>
            {loading && (
              <View
                style={{
                  position: 'absolute',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#00000040',
                }}
              >
                <BouncingDots />
              </View>
            )}
          </View>
        )}
      </View>
    </Animated.View>
  );

  const DialogBox = () => {
    if (props.spaceSelected || (props.areaSelected && !props.isSpace) || props.drawMode) {
      return null;
    }
    return (
      <>
        {menuOpen && (
          <TouchableOpacity
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backgroundColor: '#00000040',
              zIndex: 2,
            }}
            onPress={() => {
              setMenuOpen(false);
              props.setPinMenuOpen('');
            }}
          />
        )}
        <View
          style={{
            zIndex: dialogZIndex,
            position: 'absolute',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: dialogOpacity,
          }}
        >
          <RNAnimated.View
            style={{
              zIndex: infoZIndex,
              // backgroundColor: 'red', // TODO: this is a hack to make the hovered area visible
            }}
          >
            {isMobile ? <Pressable
              hitSlop={10}
              onPress={() => {
                if (props.arrowPress) {
                  props.arrowPress();
                } else {
                  setShowModal(true);
                }
              }}
            >
              <View style={styles.pinIconContainerMobile}>
                <LocationIcon solid={props.occupancyShown} />
                {props.occupancyShown && (
                  <Text style={styles.pinValueMobile}>{occupancyPinText()}</Text>
                )}
              </View>
            </Pressable> : <Animated.View style={{
              transform: [{ scale: props.negativeScale }], position: 'absolute',
            }}>
              <TouchableOpacity
                onPress={props.arrowPress}
                style={styles.pinIconContainerDesktop}>
                <LocationIconV2 solid={props.occupancyShown} />
                {props.occupancyShown && (
                  <Text style={styles.pinValueDesktop}>{occupancyPinText()}</Text>
                )}
              </TouchableOpacity>
              {/* <Text style={{
                fontSize: 12, fontFamily: 'Poppins_500Medium',
                color: '#fff', position: 'absolute', left: 15, bottom: 2,
              }}>{props.area.assetCount}</Text> */}
            </Animated.View>}
            {showLocationInfo ? locationInfo() : null}
          </RNAnimated.View>
        </View>
      </>
    );
  };

  return (
    <Animated.View key={props.index} style={transformStyleArea}>
      {isMovingThisLocation ? <Svg style={{
        width: 10000,
        height: 10000,
        top: (props.area.top + (((props.area.bottom - props.area.top) / 2) - 24)) * -1,
        left: (props.area.left + (((props.area.right - props.area.left) / 2) - 24)) * -1,
        position: 'absolute',
      }}>
        <Polygon
          points={props.area.polygon}
          fill={Colors.light.hereworksBlue200}
          opacity={0.6}
          stroke={Colors.light.hereworksBlue500}
          strokeWidth={1}
        />
      </Svg> : null}
      <DialogBox />
      <LocationView
        location={showModal ? props.area : false}
        hide={() => setShowModal(false)}
        select={props.arrowPress}
        assetsSelected={props.assetsSelected}
      />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  locationView: {
    backgroundColor: Colors.light.hereworksWhite400,
    borderColor: Colors.light.hereworksBlue100,
    borderWidth: 1,
    borderRadius: 12,
  },
  menuText: {
    fontFamily: 'Poppins_400Regular',
    paddingHorizontal: 12,
    paddingVertical: 9,
    color: Colors.light.hereworksBlack600,
    lineHeight: 21,
  },
  label: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 16,
    color: Colors.light.hereworksBlack500,
  },
  value: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 14,
    lineHeight: 16,
    color: Colors.light.hereworksBlack600,
  },
  locationType: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  nameContainer: {
    width: 160,
  },
  name: {
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    fontSize: 14,
    lineHeight: 18,
  },
  pinIconContainerMobile: {
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pinIconContainerDesktop: {
    width: 35,
    height: 43,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pinValueMobile: {
    position: 'absolute',
    top: 10,
    fontFamily: 'Roboto_500Medium',
    fontSize: 10,
    lineHeight: 12,
    color: '#fff',
    textAlign: 'center',
    minWidth: 26,
  },
  pinValueDesktop: {
    position: 'absolute',
    top: 8,
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    color: '#fff',
    textAlign: 'center',
    minWidth: 24,
  },
});
